import type { Component } from '../schemas/component';

/**
 * The order the catalog table is read in.
 *
 * `GET /api/components` returns the rows in whatever order the query produced —
 * DynamoDB's, by stored id — which is neither the order an operator created
 * them in nor one they can predict from the screen. The table is scanned by
 * eye, so it is sorted by the thing the eye is scanning: the display name.
 *
 * Out here rather than inline in the tab for the rule this workspace runs on:
 * vitest is on `environment: 'node'`, so a comparison written inside a render
 * is a comparison no test can see. A wrong one is not loud — it is a list that
 * looks fine and reshuffles.
 */

/** Locale-aware and case-insensitive: `gift aid` sorts next to `Gift Aid`, not after `Tours`. */
const byName = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

/**
 * Sorted by display name, tie-broken on id.
 *
 * The tiebreak is not decoration: two components may legitimately share a
 * display name — the catalog is unique on the id, not the name — and without it
 * the pair's order is whatever the query happened to return, so the table can
 * reshuffle between two renders of identical data.
 *
 * Copies rather than sorts in place: the array it is handed is React Query's
 * cached one, and sorting that mutates state the cache believes it owns.
 */
export function sortComponents(components: readonly Component[]): Component[] {
    return [...components].sort(
        (a, b) => byName.compare(a.displayName, b.displayName) || byName.compare(a.id, b.id),
    );
}
